import { useState, type ComponentType } from "react";
import type { BucketCount, FocusBucket } from "@ceo-cmd/shared";
import {
  ChevronDown,
  CircleAlert,
  Clock3,
  Eye,
  Layers3,
  Scale,
  Zap,
} from "lucide-react";
import { MobileSheet } from "@/components/mobile-sheet";
import { cn } from "@/cn";
import { BUCKET_LABELS } from "./labels";

const OPTIONS: Array<{
  bucket: FocusBucket;
  description: string;
  icon: ComponentType<{ className?: string }>;
}> = [
  {
    bucket: "act_now",
    description: "Moves that need action now",
    icon: Zap,
  },
  {
    bucket: "decision_required",
    description: "Decisions and approvals",
    icon: Scale,
  },
  {
    bucket: "waiting",
    description: "Waiting on someone else",
    icon: Clock3,
  },
  {
    bucket: "at_risk",
    description: "Exposure that needs attention",
    icon: CircleAlert,
  },
  {
    bucket: "monitoring",
    description: "Watch without acting yet",
    icon: Eye,
  },
  {
    bucket: "all_active",
    description: "Everything active across the portfolio",
    icon: Layers3,
  },
];

export function MobileBucketPicker({
  buckets,
  active,
  onChange,
}: {
  buckets: BucketCount[];
  active: FocusBucket;
  onChange: (bucket: FocusBucket) => void;
}) {
  const [open, setOpen] = useState(false);
  const countMap = Object.fromEntries(
    buckets.map((entry) => [entry.bucket, entry.count]),
  ) as Partial<Record<FocusBucket, number>>;
  const activeCount = countMap[active] ?? 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="flex min-h-12 w-full items-center gap-3 rounded-xl border border-border bg-card px-3.5 py-2.5 text-left shadow-sm transition-colors active:bg-surface-hover"
      >
        <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
          Queue
        </span>
        <span className="h-5 w-px bg-border" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-foreground">
          {BUCKET_LABELS[active]}
        </span>
        <span className="rounded-full bg-brand-soft px-2 py-0.5 text-xs font-semibold tabular-nums text-brand">
          {activeCount}
        </span>
        <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
      </button>

      <MobileSheet
        open={open}
        onClose={() => setOpen(false)}
        title="Choose a Today view"
        variant="bottom"
      >
        <div className="space-y-2 px-4 py-3">
          {OPTIONS.map((option) => {
            const selected = option.bucket === active;
            const Icon = option.icon;
            return (
              <button
                key={option.bucket}
                type="button"
                aria-current={selected ? "page" : undefined}
                onClick={() => {
                  onChange(option.bucket);
                  setOpen(false);
                }}
                className={cn(
                  "flex min-h-16 w-full items-center gap-3 rounded-xl border px-3.5 py-2.5 text-left transition-colors",
                  selected
                    ? "border-brand bg-brand-soft"
                    : "border-border bg-card active:bg-surface-hover",
                )}
              >
                <span
                  className={cn(
                    "flex size-9 shrink-0 items-center justify-center rounded-lg",
                    selected
                      ? "bg-brand text-brand-foreground"
                      : "bg-surface-hover text-muted-foreground",
                  )}
                >
                  <Icon className="size-4.5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-foreground">
                    {BUCKET_LABELS[option.bucket]}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {option.description}
                  </span>
                </span>
                <span
                  className={cn(
                    "min-w-8 rounded-full px-2 py-1 text-center text-xs font-semibold tabular-nums",
                    selected
                      ? "bg-brand text-brand-foreground"
                      : "bg-surface-hover text-foreground",
                  )}
                >
                  {countMap[option.bucket] ?? 0}
                </span>
              </button>
            );
          })}
        </div>
      </MobileSheet>
    </>
  );
}
